import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPinnedItems,
  getPinnableCandidates,
  addPinnedItems,
  reorderPinnedItems,
  removePinnedItem,
} from "../api/pinned";

const pinnedKey = (section) => ["pinnedItems", section];
const candidatesKey = (section, search) => ["pinnableCandidates", section, search];

/** The pinned rail for a section, in the order the public site reads it. */
export const usePinnedItems = (section) =>
  useQuery({
    queryKey: pinnedKey(section),
    queryFn: () => getPinnedItems(section),
    enabled: Boolean(section),
  });

/** Live listings that are not pinned yet — the "add" panel's source. */
export const usePinnableCandidates = (section, search, enabled = true) =>
  useQuery({
    queryKey: candidatesKey(section, search),
    queryFn: () => getPinnableCandidates(section, search),
    enabled: enabled && Boolean(section),
    keepPreviousData: true,
    staleTime: 30_000,
  });

/**
 * Every write answers with the rail as it now stands, so the cache is seeded
 * from the response rather than triggering another round trip. A rejected
 * reorder carries the same payload on the error, which is adopted too — the
 * screen must not keep showing an order the server refused.
 */
const useRailMutation = (section, mutationFn) => {
  const queryClient = useQueryClient();

  const adopt = (items) => {
    if (Array.isArray(items)) queryClient.setQueryData(pinnedKey(section), items);
  };

  return useMutation({
    mutationFn,
    onSuccess: (data) => {
      adopt(data?.items);
      // Pinning changes which listings are still available to pin.
      queryClient.invalidateQueries({ queryKey: ["pinnableCandidates", section] });
    },
    onError: (error) => adopt(error?.items),
  });
};

export const useAddPinnedItems = (section) =>
  useRailMutation(section, (items) => addPinnedItems(section, items));

export const useReorderPinnedItems = (section) =>
  useRailMutation(section, (items) => reorderPinnedItems(section, items));

export const useRemovePinnedItem = (section) =>
  useRailMutation(section, ({ category, id }) => removePinnedItem(section, category, id));
